import { gql } from "@apollo/client";
import { getSalaryCashComp } from "@asmbl/shared/compensation";
import { eq, multiply, subtract, zero } from "@asmbl/shared/money";
import { Noun } from "@asmbl/shared/permissions";
import { mapMaybe } from "@asmbl/shared/utils";
import { LinearProgress, Table, TableBody } from "@mui/material";
import makeStyles from "../../../makeStylesV4Compat";
import { useState } from "react";
import { useTrack } from "src/analytics";
import { useAuth } from "src/components/Auth/AuthContext";
import { useCompStructure } from "src/components/CompStructureContext";
import { useURLSearchParams } from "src/models/URLSearchParams";
import { useUpdateSingleEmployeePerfScore } from "src/mutations/CompCycle";
import {
  CompCycleSingleRecModal_compCycle as CompCycle,
  CompRecommendationInput,
  CompUnit,
  CurrencyCode,
  CompCycleSingleRecModal_participant as Employee,
  CompCycleSingleRecModal_compCycleBudget as OrgCompCycleBudget,
  CompCycleSingleRecModal_position as Position,
  RecItemInput,
  CompCycleSingleRecModal_valuation as Valuation,
} from "../../../__generated__/graphql";
import {
  CompRecommendation,
  CompRecTableHeaders,
  getCashRecItems,
} from "../../../models/CompRecommendation";
import { HeaderConfig } from "../../../models/HeaderConfig";
import { useEmplaceCompRecommendations } from "../../../mutations/CompRecommendation";
import { GRAY_6, WHITE } from "../../../theme";
import { useCurrencies } from "../../CurrenciesContext";
import { CompCycleBudgetBar2 } from "../CompCycleBudgetBar/CompCycleBudgetBar2";
import { CompCyclePersonRow } from "../CompTable/CompCyclePersonRow";
import { CompCycleGrouping } from "../types";
import { recommendationHasBeenTouched } from "../utils";
import { CompCycleSingleRecSubHeader } from "./CompCycleSingleRecSubHeader";
import { CompCycleTableHeaders } from "./CompCycleTableHeaders";
import { useCompRecData } from "./CompRecommendationContext";

const useStyles = makeStyles(() => ({
  table: {
    overflow: "hidden",
    tableLayout: "fixed",
    borderTop: `1px solid ${GRAY_6}`,
    zIndex: 5,
  },
  barContainer: {
    display: "flex",
    flexDirection: "row",
  },
  rightBuffer: {
    display: "flex",
    flexDirection: "column",
    width: "5%",
    boxShadow: `0px -1px 0px ${GRAY_6}`,
    backgroundColor: WHITE,
  },
  loaderWrapper: {
    height: 0,
  },
}));

interface Props {
  selectedCurrency: CurrencyCode | null;
  handleClose: () => unknown;
  compCycleId: number;
  employee: Employee;
  isIndirectReport: boolean;
  isPhaseConfigurationPeriod: boolean;
  positions: Position[];
  currentValuation: Valuation;
  compCycle: CompCycle;
  budget: OrgCompCycleBudget | null;
  revisedPerfRating: string | null;
  setRevisedPerfRating: (perfRating: string | null) => unknown;
  loading: boolean;
}

export function CompCycleSingleRecModal({
  selectedCurrency,
  handleClose,
  compCycleId,
  employee,
  isIndirectReport,
  isPhaseConfigurationPeriod,
  compCycle,
  budget,
  positions,
  currentValuation,
  revisedPerfRating,
  setRevisedPerfRating,
  loading,
}: Props): JSX.Element {
  const classes = useStyles();
  const { trackEvent } = useTrack();
  const { compStructure } = useCompStructure();
  const { permissions } = useAuth();
  const urlSearchParams = useURLSearchParams();
  const { currencies, defaultCurrency } = useCurrencies();
  const { user } = useAuth();
  const { submittedRecommendation, revisedRecommendation } = useCompRecData();

  const [budgetComponentFilter, setBudgetComponentFilter] =
    useState<CompCycleGrouping>("all");

  const managerId = urlSearchParams.get("manager");

  // if we are an HRBP then we will be submitting the request on behalf
  // of the manager we are filtering under
  const emplaceCompRecommendations = useEmplaceCompRecommendations(
    compCycleId,
    selectedCurrency,
    managerId != null && permissions.isHRBP()
      ? Number.parseInt(managerId)
      : null
  );

  const updateSingleEmployeePerfSore = useUpdateSingleEmployeePerfScore();

  const showOneTimeBonus = compCycle.allowBonus;

  const showEquityGrants = compCycle.allowEquity;

  const showSpacer = !showOneTimeBonus && !showEquityGrants;

  const userHasGlobalEditPermissions = permissions.canEditGlobal(
    Noun.CompRecommendation
  );

  // we are technically submitting and accepting the request that is made if
  // the employee being acted on is an indirect report of the user or if the
  // the current user has global edit permissions on comp recommendations
  const isSubmittingAndAccepting =
    isIndirectReport || userHasGlobalEditPermissions;

  // used to determine whether submit button is enabled during a phase
  // in cases where there is guidance or multiple reviewers
  const canUserEditInPhase =
    (compCycle.currentPhase != null &&
      submittedRecommendation?.canICurrentlyEdit) === true;

  const handleSave = async () => {
    const originalPerfRating = employee.perfRating;

    if (revisedPerfRating != null && revisedPerfRating !== originalPerfRating) {
      await updateSingleEmployeePerfSore({
        compCycleId,
        employeeId: employee.subjectId,
        perfRating: revisedPerfRating,
        note: `Changed from ${
          originalPerfRating ?? "nothing"
        } to ${revisedPerfRating} by ${user?.displayName ?? "unknown"}`,
      });
    }

    let recommendationResult;
    if (revisedRecommendation !== null) {
      recommendationResult = await emplaceCompRecommendations(
        revisedRecommendationToInput(
          revisedRecommendation,
          submittedRecommendation,
          defaultCurrency.code,
          canUserEditInPhase
        )
      );

      trackEvent({
        object: "Change Requests",
        action: "Submitted",
        compCycleId,
      });
    }

    handleClose();

    return revisedRecommendation !== null
      ? recommendationResult !== null
      : true;
  };

  const currentSalary = getSalaryCashComp(
    employee.subject.activeCashCompensation
  )?.annualCashEquivalent;

  const headerConfig: HeaderConfig = {
    [CompRecTableHeaders.AVATAR]: { weight: 1, isVisible: true },
    [CompRecTableHeaders.NAME_POS]: { weight: 3, isVisible: true },
    [CompRecTableHeaders.DEPT_LADDER]: { weight: 3, isVisible: true },
    [CompRecTableHeaders.LEVEL]: { weight: 1, isVisible: true },
    [CompRecTableHeaders.PERF_RATING]: { weight: 3, isVisible: true },
    [CompRecTableHeaders.TENURE]: { weight: 2, isVisible: true },
    [CompRecTableHeaders.LOCATION]: { weight: 2, isVisible: true },
    [CompRecTableHeaders.MANAGER]: { weight: 1.5, isVisible: true },
    [CompRecTableHeaders.TOTAL_COMP]: { weight: 3, isVisible: true },
    [CompRecTableHeaders.BAND]: { weight: 3, isVisible: true },
    [CompRecTableHeaders.BONUS]: { weight: 2, isVisible: showOneTimeBonus },
    [CompRecTableHeaders.EQUITY]: { weight: 2, isVisible: showEquityGrants },
    [CompRecTableHeaders.SPACER]: { weight: 2, isVisible: showSpacer },
  };

  const submittedItemsCashValue = getCashRecItems(
    employee.subject,
    Array.from(submittedRecommendation?.items.values() ?? []),
    compStructure?.workingHoursPerYear ?? 0,
    currentSalary ?? zero(defaultCurrency.code)
  );

  // we can safely assume that any value in the `submittedItemsCashValue` is
  // HOURLY, SALARY, or NULL
  const annualizedSubmittedItemsCashValue = submittedItemsCashValue.map(
    (item) =>
      item.unitType === CompUnit.HOURLY_CASH && item.recommendedCashValue
        ? {
            ...item,
            unitType: CompUnit.CASH,
            recommendedCashValue: multiply(
              item.recommendedCashValue,
              compStructure?.workingHoursPerYear ?? 0
            ),
          }
        : item
  );

  const revisedItemsCashValue = getCashRecItems(
    employee.subject,
    Array.from(revisedRecommendation?.items.values() ?? []),
    compStructure?.workingHoursPerYear ?? 0,
    currentSalary ?? zero(defaultCurrency.code)
  );

  // we can safely assume that any value in the `revisedItemsCashValue` is
  // HOURLY, SALARY, or NULL
  const annualizedRevisedItemsCashValue = revisedItemsCashValue.map((item) =>
    item.unitType === CompUnit.HOURLY_CASH && item.recommendedCashValue
      ? {
          ...item,
          unitType: CompUnit.CASH,
          recommendedCashValue: multiply(
            item.recommendedCashValue,
            compStructure?.workingHoursPerYear ?? 0
          ),
        }
      : item
  );

  const budgetItems =
    annualizedRevisedItemsCashValue.length > 0
      ? mapMaybe(annualizedRevisedItemsCashValue, (item) => {
          const submittedItem = annualizedSubmittedItemsCashValue.find(
            (submittedItem) =>
              submittedItem.recommendationType === item.recommendationType
          );

          if (!submittedItem) return item;

          return submittedItem.recommendedCashValue &&
            item.recommendedCashValue &&
            !eq(submittedItem.recommendedCashValue, item.recommendedCashValue)
            ? {
                ...item,
                recommendedCashValue: subtract(
                  item.recommendedCashValue,
                  submittedItem.recommendedCashValue
                ),
              }
            : null;
        })
      : [];

  return (
    <>
      {loading && (
        <div className={classes.loaderWrapper}>
          <LinearProgress variant="indeterminate" data-cy="loading-bar" />
        </div>
      )}
      <CompCycleSingleRecSubHeader
        isSubmittingAndAccepting={
          !isPhaseConfigurationPeriod && isSubmittingAndAccepting
        }
        disableSaveButton={
          loading ||
          (employee.perfRating === revisedPerfRating &&
            (revisedRecommendation === null ||
              !recommendationHasBeenTouched(
                revisedRecommendation,
                submittedRecommendation,
                canUserEditInPhase
              )))
        }
        handleCancel={handleClose}
        handleSave={handleSave}
        compCycle={compCycle}
        filter={budgetComponentFilter}
        handleFilterChange={(filter: CompCycleGrouping) =>
          setBudgetComponentFilter(filter)
        }
        displayName={employee.subject.displayName}
      />
      <Table
        stickyHeader
        className={classes.table}
        role="treegrid"
        id="review-edit-rec-modal"
      >
        <CompCycleTableHeaders headerConfig={headerConfig} />
        <TableBody>
          <CompCyclePersonRow
            compCycle={compCycle}
            key={employee.subjectId}
            employee={employee}
            positions={positions}
            currentValuation={currentValuation}
            filter={budgetComponentFilter}
            headerConfig={headerConfig}
            setRevisedPerfRating={setRevisedPerfRating}
          />
        </TableBody>
      </Table>
      <div
        className={classes.barContainer}
        data-intercom-target={"request-modal"}
      >
        <CompCycleBudgetBar2
          prefixText={
            employee.managerCompCycleBudget !== null ? "Manager's" : " Org's"
          }
          filter={budgetComponentFilter}
          valuation={currentValuation}
          compCycle={compCycle}
          budget={employee.managerCompCycleBudget ?? budget}
          recommendationItems={budgetItems}
        />
        <div className={classes.rightBuffer} />
      </div>
    </>
  );
}

const recItem = gql`
  fragment CompCycleSingleRecModal_recItem on RecItem2 {
    id
    submittedAt
    recommendationType
    recommendedCashValue
    recommendedPercentValue
    recommendedTitle
    recommendedPosition {
      id
    }
    adjustedCashBands {
      id
      name
      bandPoints {
        value {
          ... on CashValue {
            annualRate
            currencyCode
          }
        }
      }
    }
    note
    unitType
  }
`;

CompCycleSingleRecModal.fragments = {
  participant: gql`
    ${CompCyclePersonRow.fragments.participant}
    ${CompCycleBudgetBar2.fragments.compCycleBudget}
    ${recItem}
    fragment CompCycleSingleRecModal_participant on CompCycleParticipant {
      subjectId
      compCycleId
      ...CompCyclePersonRow_participant
      managerCompCycleBudget {
        ...CompCycleBudgetBar2_compCycleBudget
      }
      perfRating
      bonusIndividualPerfRating
      bonusCustomPerfRating
      compRecommendation(skipEligibility: $skipEligibility) {
        subjectId
        compCycleId
        canICurrentlyEdit(actingManagerEmployeeId: $actingManagerEmployeeId)
        latestSubmittedItems {
          ...CompCycleSingleRecModal_recItem
        }
      }
    }
  `,
  position: gql`
    ${CompCyclePersonRow.fragments.position}
    fragment CompCycleSingleRecModal_position on Position {
      ...CompCyclePersonRow_position
      id
      level
      type
      ladder {
        id
        name
      }
    }
  `,
  compCycle: gql`
    ${CompCycleBudgetBar2.fragments.compCycle}
    ${CompCyclePersonRow.fragments.compCycle}
    ${CompCycleSingleRecSubHeader.fragments.compCycle}
    fragment CompCycleSingleRecModal_compCycle on CompCycle2 {
      ...CompCyclePersonRow_compCycle
      ...CompCycleBudgetBar2_compCycle
      ...CompCycleSingleRecSubHeader_compCycle
      currentPhase {
        id
      }
      bonusGuidanceRecommendationsPrefill
      recommendationsPreFill
    }
  `,
  compCycleBudget: gql`
    ${CompCycleBudgetBar2.fragments.compCycleBudget}
    fragment CompCycleSingleRecModal_compCycleBudget on CompCycleBudget {
      ...CompCycleBudgetBar2_compCycleBudget @include(if: $isAdmin)
    }
  `,
  valuation: gql`
    ${CompCycleBudgetBar2.fragments.valuation}
    ${CompCyclePersonRow.fragments.valuation}
    fragment CompCycleSingleRecModal_valuation on Valuation {
      ...CompCyclePersonRow_valuation
      ...CompCycleBudgetBar2_valuation
    }
  `,
};

function changeUndefinedValuesToZero(
  draftItem: RecItemInput,
  currency: CurrencyCode
): RecItemInput {
  return {
    ...draftItem,
    recommendedCashValue: draftItem.recommendedCashValue ?? zero(currency),
    recommendedPercentValue: draftItem.recommendedPercentValue ?? 0,
  };
}

function revisedRecommendationToInput(
  draft: CompRecommendation,
  submitted: CompRecommendation | null,
  defaultCurrency: CurrencyCode,
  canUserEditInPhase: boolean
): CompRecommendationInput[] {
  return recommendationHasBeenTouched(
    draft,
    submitted ?? undefined,
    canUserEditInPhase
  )
    ? [
        {
          subjectId: draft.subjectId,
          items: Array.from(draft.items.values()).map((draftItem) =>
            changeUndefinedValuesToZero(draftItem, defaultCurrency)
          ),
        },
      ]
    : [];
}
